"use client";
import React, { useRef, useState, useEffect, memo, useCallback } from "react";
import type { Application } from "@splinetool/runtime";
import { useClientContext } from "@/shared/components/Context";
import s from "./styles.module.css";

import { scheduleIdleTask } from "@/shared/utils/scheduleIdleTask";

const Spline = React.lazy(() => import("@splinetool/react-spline"));

const isWebGL2Supported = () => {
  const canvas = document.createElement("canvas");
  const gl = canvas.getContext("webgl2");
  return !!gl;
};

interface Props {
  mobileSrc: string;
  desktopSrc: string;
  breakpoint?: number;
}

const DELAY_UNTIL_CANVAS_IN_DOM = 120;

export default memo(function Scene({
  mobileSrc,
  desktopSrc,
  breakpoint = 768,
}: Props) {
  const [isSupported, setIsSupported] = useState(true);

  const { setMainFigureSplineRef } = useClientContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const splineRef = useRef<Application | null>(null);
  const [isLoaded, setLoaded] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [currentSrc, setCurrentSrc] = useState<string>("");

  useEffect(() => {
    if (!isWebGL2Supported()) {
      setIsSupported(false);
      void import("@/shared/lib/bugsnagClient").then(({ default: Bugsnag }) => {
        Bugsnag.notify(new Error("WebGL 2.0 is not supported"), (event) => {
          event.severity = "info";
        });
      });
    }
  }, []);

  const updateCanvasSize = useCallback(() => {
    if (splineRef.current?.canvas) {
      const canvas = splineRef.current.canvas;
      canvas.style.width = "100%";
      canvas.style.height = "100%";
      canvas.style.objectFit = "contain";
    }
  }, []);

  const getAppropriateSrc = useCallback(() => {
    return window.innerWidth < breakpoint ? mobileSrc : desktopSrc;
  }, [mobileSrc, desktopSrc, breakpoint]);

  const handleResize = useCallback(() => {
    const newSrc = getAppropriateSrc();
    if (newSrc !== currentSrc) {
      setCurrentSrc(newSrc);
      setLoaded(false);
    }
    updateCanvasSize();
  }, [currentSrc, getAppropriateSrc, updateCanvasSize]);

  useEffect(() => {
    if (isLoaded) {
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isLoaded, handleResize]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let cancelIdleTask = () => {};

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          cancelIdleTask = scheduleIdleTask(() => {
            setCurrentSrc(getAppropriateSrc());
            setShouldLoad(true);
          }, { timeout: 1500 });
        }
      },
      { threshold: 0.1, rootMargin: "200px" }
    );

    observer.observe(container);
    return () => {
      observer.disconnect();
      cancelIdleTask();
    };
  }, [getAppropriateSrc]);

  if (!isSupported) {
    return null;
  }

  return (
    <div className={s.container} ref={containerRef}>
      <div className={`${s.wrapper} ${isLoaded ? s.ready : s.loading}`}>
        {shouldLoad && currentSrc && (
          <React.Suspense>
            <Spline
              key={currentSrc}
              scene={currentSrc}
              onLoad={(spline: Application) => {
                splineRef.current = spline;
                setMainFigureSplineRef(spline);

                setTimeout(() => {
                  updateCanvasSize();
                  setLoaded(true);
                }, DELAY_UNTIL_CANVAS_IN_DOM);
              }}
            />
          </React.Suspense>
        )}
      </div>
    </div>
  );
});
